import React, { useState } from "react";
import { CheckCircle2, XCircle, MinusCircle, ChevronDown, ChevronRight } from "lucide-react";

const PROVIDER_LABEL = { sparkpost: "SparkPost", infobip: "Infobip", google_workspace: "Workspace", cloudflare: "Cloudflare zone", cloudflare_analytics: "CF Analytics" };
const STEP_LABEL = {
  sparkpost: "SparkPost", infobip: "Infobip", google_workspace: "Google Workspace",
  cloudflare_analytics: "CF Web Analytics", turnstile: "Turnstile allowlist", cloudflare_worker: "Worker domains",
  cloudflare_email: "Email Routing", cloudflare_zone: "Cloudflare zone", registrar: "Registrar", r2: "R2 assets", database: "Database",
};
const OPTION_META = [
  ["email_providers", "Email providers", "SparkPost sending/tracking domains · Infobip domain · Workspace alias"],
  ["cloudflare", "Cloudflare", "Worker custom domains · Email Routing · Web Analytics · Turnstile allowlist · zone (all DNS)"],
  ["registrar", "Registrar", "Cloudflare Registrar auto-renew OFF · Namecheap nameservers reset to default"],
  ["r2", "R2 assets", "Article images · logos · SSR snapshots"],
];

export function DeletePlanRows({ targets }) {
  return (
    <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800 max-h-56 overflow-y-auto" data-testid="bulk-delete-plan">
      {targets.map((t) => (
        <div key={t.site_id} className="px-3 py-2 text-xs" data-testid={`bulk-delete-row-${t.site_id}`}>
          <div className="flex items-center gap-2">
            <span className="text-zinc-100 font-semibold truncate">{t.domain || t.name}</span>
            {t.live && <span className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-500/40 px-1 rounded-sm" data-testid={`bulk-delete-live-${t.site_id}`}>live</span>}
            <span className="ml-auto mono text-zinc-500">{t.published} published · {t.articles} total</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {t.registrar && <span className="text-[10px] mono text-zinc-400 border border-zinc-800 px-1 rounded-sm">registrar: {t.registrar}</span>}
            {t.providers.map((p) => <span key={p} className="text-[10px] mono text-sky-300 border border-sky-500/30 px-1 rounded-sm">{PROVIDER_LABEL[p] || p}</span>)}
            {!t.providers.length && <span className="text-[10px] text-zinc-600">nothing connected — DB only</span>}
          </div>
          {t.notes.map((nt) => <div key={nt} className="mt-1 text-[11px] text-amber-200/80">⚠ {nt}</div>)}
        </div>
      ))}
    </div>
  );
}

export function DeleteOptions({ opts, setOpts }) {
  return (
    <div className="space-y-1.5" data-testid="bulk-delete-options">
      {OPTION_META.map(([k, label, hint]) => (
        <label key={k} className="flex items-start gap-2 text-sm text-zinc-200 cursor-pointer">
          <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts((o) => ({ ...o, [k]: e.target.checked }))} data-testid={`bulk-delete-opt-${k}`} className="mt-0.5" />
          <span>{label} <span className="block text-[11px] text-zinc-500">{hint}</span></span>
        </label>
      ))}
    </div>
  );
}

function StepIcon({ status }) {
  if (status === "ok") return <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />;
  if (status === "failed") return <XCircle size={12} className="text-rose-400 shrink-0" />;
  return <MinusCircle size={12} className="text-zinc-600 shrink-0" />;
}

function ResultRow({ r }) {
  const [open, setOpen] = useState(r.ok === false);
  const steps = Object.entries(r.steps || {});
  return (
    <div className="text-xs" data-testid={`bulk-delete-result-${r.site_id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-900/60 text-left">
        {open ? <ChevronDown size={12} className="text-zinc-500" /> : <ChevronRight size={12} className="text-zinc-500" />}
        {r.ok === false ? <XCircle size={13} className="text-rose-400 shrink-0" /> : <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />}
        <span className="truncate flex-1 text-zinc-200">{r.domain || r.name || r.site_id}</span>
        <span className="text-zinc-500 truncate max-w-[45%]">{r.error || r.reason}</span>
      </button>
      {open && steps.length > 0 && (
        <div className="px-8 pb-2 space-y-0.5">
          {steps.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[11px]">
              <StepIcon status={v.status} />
              <span className="text-zinc-300 w-32 shrink-0">{STEP_LABEL[k] || k}</span>
              <span className={v.status === "failed" ? "text-rose-300 truncate" : "text-zinc-500 truncate"}>{v.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DeleteJobResults({ job }) {
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  const running = job.status === "running";
  return (
    <div className="space-y-2" data-testid="bulk-delete-job">
      <div className="flex justify-between text-xs">
        <span className={running ? "text-amber-300" : "text-emerald-400"}>{running ? "Tearing down…" : "Finished"}</span>
        <span className="mono text-zinc-400" data-testid="bulk-delete-progress">{job.done}/{job.total} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-sm overflow-hidden"><div className="h-full bg-rose-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800 max-h-72 overflow-y-auto" data-testid="bulk-delete-results">
        {(job.results || []).map((r) => <ResultRow key={r.site_id} r={r} />)}
      </div>
      {!running && <div className="text-[11px] text-zinc-500">Failed steps are recorded in the teardown log for manual clean-up; the sites themselves are gone.</div>}
    </div>
  );
}
